import { mergeStyles } from '@cian/utils';
import * as React from 'react';

import { Icon, TIconType } from '../Icon';
import { Text } from '../Text';
import { TextAndIcon } from '../TextAndIcon';

import * as styles from './RadioButtonsMultiple.css';

import { TColors } from '../../types/colors';

interface IRadioButtonsStackProps extends React.HTMLProps<HTMLInputElement> {
  sync?: boolean;
  icon?: TIconType;
  text?: string;
}

interface IExtandedStackProps extends IRadioButtonsStackProps {
  disabled?: boolean;
}

export interface IRadioButtonsMultipleProps extends React.HTMLProps<HTMLInputElement> {
  stack: IRadioButtonsStackProps[];
  name: string;
  value?: string;
  dataMark?: string;
  onChange?(e: React.ChangeEvent<HTMLInputElement>): void;
  onClick?(e: React.MouseEvent<HTMLInputElement>): void;
}

const TEXT_COLOR: TColors = 'black';

export class RadioButtonsMultiple extends React.PureComponent<IRadioButtonsMultipleProps> {
  public static defaultProps = {
    onChange: () => {},
    onClick: () => {},
  };

  public render() {
    const { stack, dataMark = 'RadioButtonsMultiple', ...rest } = this.props;

    return (
      <div
        {...mergeStyles(
          styles['radio_buttons_multiple'],
          rest && rest.disabled && styles['disabled'],
        )}
        data-mark={dataMark}
      >
         {
            stack
              .map(this.extendedProps)
              .map(this.getRadioButton)
          }
      </div>
    );
  }

  private getRadioButton = (props: IExtandedStackProps, index: number) => {
    const { icon, text, sync, ...rest } = props;

    return (
      <label
        {...this.mixTypesStyles({ icon, text, sync })}
        key={index}
      >
        <input
          type="radio"
          className={styles['input']}
          {...rest}
        />
        <span className={styles['button']}>
          {this.getButtonContent(text, icon)}
          {
            sync && (
              <span className={styles['loader']}>
                <Icon type="loader" color={TEXT_COLOR} size={16}/>
              </span>
            )
          }
        </span>
      </label>
    );
  }

  private extendedProps = ({ text, icon, value, ...other }: IRadioButtonsStackProps): IExtandedStackProps => ({
    disabled: this.props.disabled,
    icon,
    name: this.props.name,
    checked: this.props.value === value,
    onChange: this.props.onChange,
    onClick: this.props.onClick,
    text,
    value,
    ...other,
  })

  private getButtonContent = (text?: string, icon?: TIconType) => {
    if (icon && text) {
      return (
        <TextAndIcon
          iconType={icon}
          color={TEXT_COLOR}
          colorHovered={TEXT_COLOR}
          iconColor={TEXT_COLOR}
          iconColorHovered={TEXT_COLOR}
          iconSize={14}
          fontSize={14}
          lineHeight={14}
          reverse
        >
          {text}
        </TextAndIcon>
      );
    } else if (icon) {
      return <Icon
        type={icon}
        color={TEXT_COLOR}
        size={14}
      />;
    }

    return <Text
      color={TEXT_COLOR}
      fontSize={14}
      lineHeight={14}
    >
      {text}
    </Text>;
  }

  private mixTypesStyles = (rest?: IRadioButtonsStackProps) => mergeStyles([
    styles['label'],
    rest && !rest.text && rest.icon && styles['only_icon_button'],
    rest && rest.sync && styles['sync'],
  ])
}
